import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { getPlans, formatEUR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";
import { PaypalBadge } from "@/components/PaypalBadge";
import { PRICING } from "@/constants/testIds";

export const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const { selectPlan } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    getPlans().then(setPlans).catch(() => {});
  }, []);

  const choose = (p) => {
    selectPlan(p);
    navigate("/checkout");
  };

  return (
    <section id="prezzi" data-testid={PRICING.section} className="py-16 sm:py-24 bg-secondary/40 border-y border-border">
      <div className="container-x">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-semibold text-accent uppercase tracking-wider">Prezzi</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">Scegli la durata del pannello</h2>
          <p className="mt-4 text-muted-foreground text-base leading-relaxed">
            Tutti i piani includono l'intera suite di funzionalità di controllo parentale. Cambia solo la durata: più mesi, più risparmio.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 items-stretch">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              className="h-full"
            >
              <Card
                data-testid={PRICING.card}
                className={`relative h-full p-6 rounded-2xl flex flex-col card-lift ${
                  p.highlight
                    ? "border-2 border-accent glow-strong bg-card"
                    : "border-border glow-hover bg-card"
                }`}
              >
                {p.badge && (
                  <Badge className={`absolute -top-3 left-6 rounded-lg ${p.highlight ? "bg-accent text-accent-foreground glow" : "bg-primary text-primary-foreground"}`}>
                    {p.highlight && <Sparkles className="h-3 w-3" />} {p.badge}
                  </Badge>
                )}
                <h3 className="text-lg font-semibold">{p.name}</h3>
                <p className="mt-1 text-xs text-muted-foreground min-h-[2.5rem]">{p.description}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-4xl font-semibold">{formatEUR(p.price)}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatEUR(p.price_per_month)} / mese
                </p>
                <PaypalBadge testId={`paypal-badge-plan-${p.slug}`} className="mt-3" />

                <Button
                  data-testid={PRICING.cta}
                  onClick={() => choose(p)}
                  className={`mt-5 w-full rounded-xl glow-hover ${p.highlight ? "" : ""}`}
                  variant={p.highlight ? "default" : "outline"}
                >
                  Acquista ora
                </Button>

                <ul className="mt-6 space-y-2.5">
                  {p.features.slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                  <li className="flex items-start gap-2 text-xs font-medium text-foreground">
                    <Check className="h-3.5 w-3.5 text-accent mt-0.5 shrink-0" />
                    Tutte le funzioni di controllo parentale incluse
                  </li>
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
